import React, { useState } from "react";
import { useAnalytics } from "../../../hooks/useAnalytics";

const DAY_OPTIONS = [7, 15, 30];

const StatCard = ({ title, value, icon, color }) => (
  <div className={`stat shadow ${color} text-white rounded-lg`}>
    <div className="stat-figure text-3xl">
      <i className={`fa-solid ${icon}`}></i>
    </div>
    <div className="stat-title text-white">{title}</div>
    <div className="stat-value">{value ?? "—"}</div>
  </div>
);

const AdminAnalytics = () => {
  const [days, setDays] = useState(7);
  const { data, isLoading, error } = useAnalytics(days);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">API Requests Analytics</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-28 rounded-lg"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="skeleton h-64 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Failed to load analytics: {error.message}</span>
      </div>
    );
  }

  const { overview, statusBreakdown, requestsByDay, topRoutes, requestsByMethod, activeUsers } = data?.data || {};

  const statusColors = {
    200: "badge-success",
    201: "badge-success",
    400: "badge-warning",
    401: "badge-error",
    403: "badge-error",
    404: "badge-warning",
    500: "badge-error",
  };

  const methodColors = {
    GET: "badge-info",
    POST: "badge-success",
    PUT: "badge-warning",
    PATCH: "badge-warning",
    DELETE: "badge-error",
  };

  const maxDayCount = Math.max(...(requestsByDay?.map((d) => d.count) || [1]));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">API Requests Analytics</h1>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Requests" value={overview?.totalAll?.toLocaleString()} icon="fa-chart-line" color="bg-blue-600" />
        <StatCard title="Today" value={overview?.totalToday?.toLocaleString()} icon="fa-calendar-day" color="bg-green-600" />
        <StatCard title={`Last ${days} Days`} value={overview?.totalChart?.toLocaleString()} icon="fa-calendar-week" color="bg-purple-600" />
        <StatCard title="Avg Response" value={`${overview?.avgResponseTime || 0}ms`} icon="fa-clock" color="bg-red-600" />
      </div>

      {/* Requests by Day */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <h2 className="card-title text-sm">Requests (Last {days} Days)</h2>
            <div className="btn-group">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  className={`btn btn-sm ${days === d ? "btn-active" : ""}`}
                  onClick={() => setDays(d)}
                >
                  {d} Days
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end gap-1 h-40 mt-2">
            {requestsByDay?.map((day) => (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-gray-600">{day.count}</span>
                <div
                  className="w-full bg-blue-500 rounded-t"
                  style={{ height: `${(day.count / maxDayCount) * 100}%`, minHeight: "4px" }}
                ></div>
                <span className="text-xs text-gray-500 truncate w-full text-center">
                  {day.date.slice(5)}
                </span>
              </div>
            ))}
            {(!requestsByDay || requestsByDay.length === 0) && (
              <div className="text-center w-full text-gray-500 py-8">No data yet</div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Status Breakdown */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Status Codes (30 Days)</h2>
            <div className="space-y-2 mt-2">
              {statusBreakdown?.map((s) => (
                <div key={s.status} className="flex items-center justify-between">
                  <span className={`badge ${statusColors[s.status] || "badge-ghost"} badge-sm`}>
                    {s.status}
                  </span>
                  <div className="flex-1 mx-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full"
                        style={{
                          width: `${(s.count / (overview?.totalMonth || 1)) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-sm font-medium w-16 text-right">{s.count.toLocaleString()}</span>
                </div>
              ))}
              {(!statusBreakdown || statusBreakdown.length === 0) && (
                <div className="text-center text-gray-500 py-4">No data yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Method Breakdown */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">HTTP Methods (30 Days)</h2>
            <div className="space-y-2 mt-2">
              {requestsByMethod?.map((m) => (
                <div key={m.method} className="flex items-center justify-between">
                  <span className={`badge ${methodColors[m.method] || "badge-ghost"} badge-sm`}>
                    {m.method}
                  </span>
                  <span className="text-sm font-medium">{m.count.toLocaleString()}</span>
                </div>
              ))}
              {(!requestsByMethod || requestsByMethod.length === 0) && (
                <div className="text-center text-gray-500 py-4">No data yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Top Routes */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Top Routes (30 Days)</h2>
            <div className="overflow-x-auto mt-2">
              <table className="table table-zebra table-sm">
                <thead>
                  <tr>
                    <th>Route</th>
                    <th className="text-right">Hits</th>
                    <th className="text-right">Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {topRoutes?.map((r) => (
                    <tr key={r.path}>
                      <td className="text-xs font-mono">{r.path}</td>
                      <td className="text-right text-xs">{r.count.toLocaleString()}</td>
                      <td className="text-right text-xs">{r.avgDuration}ms</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!topRoutes || topRoutes.length === 0) && (
                <div className="text-center text-gray-500 py-4">No data yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Active Users */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Most Active Users (30 Days)</h2>
            <div className="space-y-2 mt-2">
              {activeUsers?.map((u, i) => (
                <div key={u.email} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                    <span className="text-xs truncate max-w-[200px]">{u.email}</span>
                  </div>
                  <span className="badge badge-sm">{u.count}</span>
                </div>
              ))}
              {(!activeUsers || activeUsers.length === 0) && (
                <div className="text-center text-gray-500 py-4">No data yet</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
